import { z } from "zod"
import { zAccessDeniedError } from "../generated-backend/zod.gen"
import { zErrorModel } from "../generated-social/zod.gen"

// Backend and social answer 403 with different bodies; social may omit permission.
const accessDeniedBodySchema = z.union([
	zAccessDeniedError.pick({ code: true, permission: true }),
	z.object({
		code: zErrorModel.shape.code.exclude(["not_ready"]),
		permission: zErrorModel.shape.permission.unwrap().nullable().default(null),
	}),
])

type AccessDeniedBody = z.infer<typeof accessDeniedBodySchema>

type RequestTarget = { path: string | undefined; method: string }

/**
 * Достоверного ответа нет: обрыв связи, заморозка WebView, DNS/TLS, CORS.
 * Движки называют это по-разному ("Load failed" в WebKit, "Failed to fetch" в
 * Blink) и бросают из нативного кода, поэтому стека у исходной ошибки нет.
 *
 * Отличать от HttpError нужно для ретраев, но означает это лишь «ответ не
 * получен» — запрос мог дойти и примениться. Повтор безопасен для чтений,
 * для команд — только при идемпотентности на бэке.
 */
export class NetworkError extends Error {
	path: string | undefined
	method: string | undefined
	constructor(cause: unknown, request?: RequestTarget) {
		super("Network request failed", { cause })
		this.name = "NetworkError"
		this.path = request?.path
		this.method = request?.method
	}
}

export function isNetworkError(error: unknown): error is NetworkError {
	return error instanceof NetworkError
}

/**
 * Отмена (cancelQueries, AbortSignal) — не сбой, возвращаем как есть, иначе
 * ретраи начнут воскрешать отменённые запросы. Проверяем name, а не
 * `instanceof DOMException`: ошибка из другого realm его не пройдёт.
 */
export function toNetworkError(
	cause: unknown,
	request?: RequestTarget,
): unknown {
	if (isAbortError(cause)) return cause
	return new NetworkError(cause, request)
}

export function isAbortError(cause: unknown): boolean {
	return (
		typeof cause === "object" &&
		cause !== null &&
		"name" in cause &&
		cause.name === "AbortError"
	)
}

export class HttpError extends Error {
	status: number
	info?: unknown
	path: string | undefined
	method: string | undefined
	constructor(
		message: string,
		status: number,
		info?: unknown,
		request?: RequestTarget,
	) {
		super(message)
		this.name = "HttpError"
		this.status = status
		this.info = info
		this.path = request?.path
		this.method = request?.method
	}
}

export class AccessDeniedError extends HttpError {
	code: AccessDeniedBody["code"]
	permission: AccessDeniedBody["permission"]
	constructor(
		body: AccessDeniedBody,
		message: string,
		info?: unknown,
		request?: RequestTarget,
	) {
		super(message, 403, info, request)
		this.name = "AccessDeniedError"
		this.code = body.code
		this.permission = body.permission
	}
}

// Сообщение об ошибке бэкенды кладут по-разному: message у backend, detail у social.
const errorBodySchema = z.object({
	message: z.string().optional().catch(undefined),
	detail: z.string().optional().catch(undefined),
})

export function toHttpError(
	status: number,
	data: unknown,
	request?: RequestTarget,
): HttpError {
	const body = errorBodySchema.safeParse(data)
	const message =
		body.data?.message ??
		body.data?.detail ??
		`Request failed with status ${status}`
	if (status === 403) {
		const denied = accessDeniedBodySchema.safeParse(data)
		if (denied.success) {
			return new AccessDeniedError(denied.data, message, data, request)
		}
	}
	return new HttpError(message, status, data, request)
}

export function isHttpError(error: unknown): error is HttpError {
	return error instanceof HttpError
}

/**
 * Запрос не удался — обрыв связи или ответ с ошибкой. Противоположность —
 * сломанный код: там повторять нечего, нужен экран краша со стеком.
 */
export function isRequestError(
	error: unknown,
): error is NetworkError | HttpError {
	return isNetworkError(error) || isHttpError(error)
}
